/**
 * PostPreviewDrawer.tsx — Slide-out panel: caption + PDF preview
 *
 * Opens on [Preview] click in the dashboard.
 * Fetches GET /api/campaigns/:id/posts/:pid/pdf then renders via LinkedInPostPreview.
 *
 * RI-006: Drawer stays open (captionText visible) while PDF is loading — no collapse.
 * LinkedInPostPreview already uses usePdfCanvas internally — no new canvas code here.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import LinkedInPostPreview from "../../components/LinkedInPostPreview";
import { ACCENT } from "../DistillPageV2/sharedStyles";
import type { CampaignPost } from "./types";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  campaignId: number;
  post:       CampaignPost | null;  // null = drawer closed
  onClose:    () => void;
  authHeaders: Record<string, string>;
  apiUrl:     string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// ── Component ─────────────────────────────────────────────────────────────────

const PostPreviewDrawer: React.FC<Props> = ({
  campaignId, post, onClose, authHeaders, apiUrl,
}) => {
  const [pdfBase64,   setPdfBase64]   = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError,    setPdfError]    = useState<string | null>(null);

  // Fetch PDF whenever the open post changes
  useEffect(() => {
    if (!post) {
      setPdfBase64(null);
      setPdfError(null);
      return;
    }

    let cancelled = false;
    setPdfBase64(null);
    setPdfError(null);
    setIsLoadingPdf(true);

    axios.get<{ pdfBase64: string }>(
      `${apiUrl}/api/campaigns/${campaignId}/posts/${post.id}/pdf`,
      { withCredentials: true, headers: authHeaders }
    )
      .then(r => {
        if (!cancelled) {
          setPdfBase64(r.data.pdfBase64);
          setIsLoadingPdf(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPdfError("Couldn't load preview");
          setIsLoadingPdf(false);
        }
      });

    return () => { cancelled = true; };
  }, [post?.id, campaignId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!post) return null;

  // slides may not be in the GET /campaigns/:id response — default to 1 so nav arrows work
  // correctly if the backend ever starts returning the count.
  const totalSlides = post.slides?.length ?? 1;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="cp-drawer-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.25)",
          zIndex: 800,
        }}
      />

      {/* Drawer panel */}
      <div
        data-testid="cp-preview-drawer"
        style={{
          position:   "fixed",
          top:        0,
          right:      0,
          bottom:     0,
          width:      "min(460px, 95vw)",
          background: "#fff",
          boxShadow:  "-4px 0 32px rgba(0,0,0,0.12)",
          zIndex:     801,
          display:    "flex",
          flexDirection: "column",
          overflow:   "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "16px 20px",
          borderBottom:   "1px solid #E4E4E0",
          flexShrink:     0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "15px" }}>
              Day {post.dayNumber} Preview
            </div>
            <div style={{ fontSize: "12px", color: "#aaa", marginTop: "2px" }}>
              Scheduled: {formatDate(post.scheduledAt)}
            </div>
          </div>
          <button
            data-testid="cp-drawer-close"
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "20px", color: "#888", padding: "4px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content — scrollable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {pdfError && (
            <div style={{
              background: "#FFF0F0", border: "1.5px solid #E84F4F",
              borderRadius: "10px", padding: "12px 14px",
              fontSize: "13px", color: "#C0392B", marginBottom: "16px",
            }}>
              {pdfError}
            </div>
          )}

          {/* RI-006: LinkedInPostPreview renders immediately with captionText.
              pdfBase64=null shows a placeholder canvas — drawer never collapses. */}
          <LinkedInPostPreview
            postText={post.captionText}
            pdfBase64={pdfBase64}
            totalSlides={totalSlides}
            documentTitle={`Day ${post.dayNumber}`}
            standalone={true}
            isAdmin={false}
          />

          {isLoadingPdf && !pdfBase64 && (
            <div style={{
              textAlign: "center", color: "#aaa",
              fontSize: "13px", marginTop: "12px",
            }}>
              Loading preview…
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PostPreviewDrawer;
